import { useState, useRef, useCallback, useEffect } from 'react';

interface Position {
  x: number;
  y: number;
}

export const useDraggable = (initialPosition: Position = { x: 0, y: 0 }) => {
  const [position, setPosition] = useState<Position>(initialPosition);
  const isDragging = useRef(false);
  const wasDragged = useRef(false);
  const dragStartPos = useRef<Position>({ x: 0, y: 0 });
  const startPos = useRef<Position>({ x: 0, y: 0 });

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLElement>) => {
    if (e.button !== 0) return;
    
    // Ignore drags that start on interactive elements like buttons
    const target = e.target as HTMLElement;
    
    if (target.closest('.allow-drag')) {
      // Explicitly allowed to drag
    } else if (target.closest('button') || target.closest('a') || target.closest('svg') || target.closest('path')) {
      // If it's the drag handle itself or an icon in the header, we want to allow it unless it's inside an IconButton
      if (target.closest('.MuiIconButton-root') || target.closest('button')) {
        return;
      }
    }
    
    const currentTarget = e.currentTarget;
    currentTarget.setPointerCapture(e.pointerId);

    isDragging.current = true;
    wasDragged.current = false;
    dragStartPos.current = { x: e.clientX, y: e.clientY };
    startPos.current = { ...position };
  }, [position]);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLElement>) => {
    if (!isDragging.current) return;
    
    const dx = e.clientX - dragStartPos.current.x;
    const dy = e.clientY - dragStartPos.current.y;

    if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
      wasDragged.current = true;
    }

    if (wasDragged.current) {
      e.preventDefault();
      setPosition({
        x: startPos.current.x + dx,
        y: startPos.current.y + dy,
      });
    }
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent<HTMLElement>) => {
    if (isDragging.current) {
      isDragging.current = false;
      const target = e.currentTarget;
      target.releasePointerCapture(e.pointerId);
    }
  }, []);

  useEffect(() => {
    const handleMouseUp = () => {
      isDragging.current = false;
    };
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, []);

  const resetPosition = useCallback(() => {
    setPosition(initialPosition);
  }, [initialPosition]);

  return {
    position,
    setPosition,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    resetPosition,
    wasDragged
  };
};
